/**
 * StrategyLens — the primary lens of BriefWorkspace.
 *
 * Default view: **Synthesized** — a rich, LLM-rendered editorial strategy
 * document generated by Gemini 2.5 Pro. Displays headline, strategic thesis,
 * persona lens, key tensions, creative mandate, winning angles, and a
 * generated brief identity icon.
 *
 * Toggle view (Settings icon in top-right): **Sections** — the existing
 * BriefCanvas (8-section structured editing + StrategicGroundingPanel).
 *
 * The synthesis is fetched lazily on first render and cached by the backend
 * via an inputs_hash. Re-synthesizing is triggered by the "Refresh" button.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  BookOpen,
  ChevronRight,
  Flame,
  Lightbulb,
  Loader2,
  RefreshCw,
  Settings2,
  Shield,
  Sparkles,
  Target,
  Zap,
} from 'lucide-react';
import {
  BriefCanvas,
  SECTION_ORDER,
  type BriefDraftSections,
  type BriefSnapshot,
  type SectionKey,
} from '@neurograph/brief-ui';
import type { GenerationBrief, SynthesizedStrategy } from '@/services/api';
import { generationApi } from '@/services/api';
import type { UseBriefWorkspaceChatReturn } from '../useBriefWorkspaceChat';
import { workspaceBriefApi } from '../useBriefWorkspaceChat';
import { StrategicGroundingPanel } from '../panels/StrategicGroundingPanel';

// ─── Types ──────────────────────────────────────────────────────────────────

interface StrategyLensProps {
  brief: GenerationBrief;
  chat: UseBriefWorkspaceChatReturn;
  recentlyPatched: Set<string>;
  onComposerPrefill: (text: string) => void;
  /** Called after a synthesis is fetched so the store can be updated */
  onSynthesized?: (updated: GenerationBrief) => void;
  /** Called to navigate to the Angles tab from the grounding panel CTA. */
  onNavigateToAngles?: () => void;
}

// ─── Helper: map draft value to section content (carried from DocumentLens) ─

function mapDraftValueToContent(
  section: SectionKey,
  value: string,
): Record<string, unknown> {
  switch (section) {
    case 'objective':
      return { statement: value };
    case 'audience':
      return { one_liner: value };
    case 'insight':
      return { tension: value };
    default:
      return { summary: value };
  }
}

// ─── Sub-components: Synthesized editorial layout ───────────────────────────

function SectionPill({
  icon: Icon,
  label,
  color,
}: {
  icon: React.ElementType;
  label: string;
  color: string;
}) {
  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${color}`}>
      <Icon size={11} />
      {label}
    </div>
  );
}

function TensionCard({ tension, index }: { tension: string; index: number }) {
  const colors = [
    'bg-violet-50 border-violet-200 text-violet-800',
    'bg-rose-50 border-rose-200 text-rose-800',
    'bg-amber-50 border-amber-200 text-amber-800',
    'bg-teal-50 border-teal-200 text-teal-800',
  ];
  return (
    <div className={`rounded-xl border px-4 py-3 text-sm font-medium leading-snug ${colors[index % colors.length]}`}>
      <span className="opacity-40 font-mono mr-2 text-xs">{String(index + 1).padStart(2, '0')}</span>
      {tension}
    </div>
  );
}

function WinningAngleCard({
  hook,
  rationale,
  scenarioName,
}: {
  hook: string;
  rationale: string;
  scenarioName?: string | null;
}) {
  return (
    <div className="flex-none w-72 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow">
      {scenarioName && (
        <div className="text-[10px] font-semibold uppercase tracking-widest text-teal-600 mb-2">
          {scenarioName}
        </div>
      )}
      <p className="text-sm font-semibold text-slate-900 leading-snug mb-2 line-clamp-3">
        &ldquo;{hook}&rdquo;
      </p>
      <p className="text-xs text-slate-500 leading-relaxed line-clamp-3">{rationale}</p>
    </div>
  );
}

function EvidenceChip({ claim, sourceType, sourceLabel }: { claim: string; sourceType: string; sourceLabel: string }) {
  const typeColors: Record<string, string> = {
    persona: 'bg-violet-100 text-violet-700',
    insight: 'bg-amber-100 text-amber-700',
    angle: 'bg-teal-100 text-teal-700',
    product: 'bg-sky-100 text-sky-700',
    brand_voice: 'bg-rose-100 text-rose-700',
    performance: 'bg-emerald-100 text-emerald-700',
  };
  const chipColor = typeColors[sourceType] ?? 'bg-slate-100 text-slate-600';
  return (
    <div className="group relative">
      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium cursor-default ${chipColor}`}>
        <span className="opacity-50 text-[9px] uppercase font-bold">{sourceLabel}</span>
        <span className="truncate max-w-[200px]">{claim}</span>
      </div>
    </div>
  );
}

function BriefIdentityIcon({ dataUrl, size = 64 }: { dataUrl?: string | null; size?: number }) {
  if (!dataUrl) {
    return (
      <div
        className="rounded-2xl bg-gradient-to-br from-teal-400 to-violet-500 flex items-center justify-center flex-shrink-0"
        style={{ width: size, height: size }}
      >
        <Sparkles size={size * 0.4} className="text-white opacity-80" />
      </div>
    );
  }
  return (
    <img
      src={dataUrl}
      alt="Brief identity"
      className="rounded-2xl object-cover flex-shrink-0"
      style={{ width: size, height: size }}
    />
  );
}

// Skeleton shimmers while the synthesis is loading
function SynthesisSkeleton() {
  return (
    <div className="animate-pulse space-y-6 p-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-5">
        <div className="w-16 h-16 rounded-2xl bg-slate-200" />
        <div className="flex-1 space-y-2">
          <div className="h-7 bg-slate-200 rounded-lg w-2/3" />
          <div className="h-4 bg-slate-100 rounded w-1/3" />
        </div>
      </div>
      {/* Thesis */}
      <div className="space-y-2">
        <div className="h-4 bg-slate-200 rounded w-full" />
        <div className="h-4 bg-slate-200 rounded w-5/6" />
        <div className="h-4 bg-slate-200 rounded w-4/6" />
      </div>
      {/* Three column */}
      <div className="grid grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-28 bg-slate-100 rounded-2xl" />
        ))}
      </div>
      {/* Angles scroll */}
      <div className="flex gap-4 overflow-hidden">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex-none w-72 h-36 bg-slate-100 rounded-2xl" />
        ))}
      </div>
    </div>
  );
}

// ─── Main synthesized editorial view ────────────────────────────────────────

function SynthesizedView({
  synthesis,
  onRefresh,
  isRefreshing,
}: {
  synthesis: SynthesizedStrategy;
  onRefresh: () => void;
  isRefreshing: boolean;
}) {
  const formattedAt = useMemo(() => {
    try {
      return new Date(synthesis.synthesized_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  }, [synthesis.synthesized_at]);

  return (
    <div className="max-w-5xl mx-auto p-8 space-y-10">
      {/* ── Header: Identity image + Headline ── */}
      <div className="flex items-start gap-6">
        <BriefIdentityIcon dataUrl={synthesis.icon_data_url} size={72} />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4">
            <h1 className="text-2xl font-bold text-slate-900 leading-tight tracking-tight">
              {synthesis.headline}
            </h1>
            <button
              onClick={onRefresh}
              disabled={isRefreshing}
              className="flex-none flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-40 mt-1"
              title="Re-synthesize with latest brief context"
            >
              <RefreshCw size={12} className={isRefreshing ? 'animate-spin' : ''} />
              {isRefreshing ? 'Synthesizing…' : 'Refresh'}
            </button>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <SectionPill icon={Sparkles} label="Gemini Synthesized" color="bg-violet-50 text-violet-700" />
            <span className="text-xs text-slate-400">{formattedAt}</span>
          </div>
        </div>
      </div>

      {/* ── Strategic Thesis ── */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <Target size={14} className="text-teal-500" />
          <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-500">Strategic Thesis</h2>
        </div>
        <div className="relative rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 p-6 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-0 right-0 w-64 h-64 bg-teal-400 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-violet-400 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl" />
          </div>
          <p className="relative text-white text-base leading-relaxed font-light">
            {synthesis.strategic_thesis}
          </p>
        </div>
      </section>

      {/* ── Three-column: Persona Lens | Key Tensions | Creative Mandate ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Persona Lens */}
        <section className="rounded-2xl border border-violet-100 bg-violet-50/50 p-5">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen size={13} className="text-violet-500" />
            <h3 className="text-xs font-semibold uppercase tracking-widest text-violet-600">Persona Lens</h3>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">{synthesis.persona_lens}</p>
        </section>

        {/* Key Tensions */}
        <section className="rounded-2xl border border-amber-100 bg-amber-50/50 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Zap size={13} className="text-amber-500" />
            <h3 className="text-xs font-semibold uppercase tracking-widest text-amber-600">Key Tensions</h3>
          </div>
          <div className="space-y-2">
            {synthesis.key_tensions.map((tension, i) => (
              <TensionCard key={i} tension={tension} index={i} />
            ))}
            {synthesis.key_tensions.length === 0 && (
              <p className="text-xs text-amber-500/60 italic">No tensions identified</p>
            )}
          </div>
        </section>

        {/* Creative Mandate */}
        <section className="rounded-2xl border border-teal-100 bg-teal-50/50 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Flame size={13} className="text-teal-500" />
            <h3 className="text-xs font-semibold uppercase tracking-widest text-teal-600">Creative Mandate</h3>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed font-medium">
            {synthesis.creative_mandate}
          </p>
          {synthesis.brand_voice_signature && (
            <div className="mt-4 pt-3 border-t border-teal-100">
              <p className="text-[10px] text-teal-500 uppercase tracking-widest font-semibold mb-1">Voice Signature</p>
              <p className="text-xs text-slate-600 italic">{synthesis.brand_voice_signature}</p>
            </div>
          )}
        </section>
      </div>

      {/* ── Winning Angles (horizontal scroll) ── */}
      {synthesis.winning_angles.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb size={14} className="text-amber-500" />
            <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              Winning Angles
            </h2>
            <span className="text-xs text-slate-400">({synthesis.winning_angles.length})</span>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-2 -mx-2 px-2 scrollbar-thin scrollbar-thumb-slate-200">
            {synthesis.winning_angles.map((angle, i) => (
              <WinningAngleCard
                key={i}
                hook={angle.hook}
                rationale={angle.rationale}
                scenarioName={angle.scenario_name}
              />
            ))}
          </div>
        </section>
      )}

      {/* ── Product Intelligence ── */}
      {synthesis.product_intelligence && (
        <section className="rounded-2xl border border-sky-100 bg-sky-50/40 px-6 py-4">
          <div className="flex items-center gap-2 mb-2">
            <Target size={13} className="text-sky-500" />
            <h3 className="text-xs font-semibold uppercase tracking-widest text-sky-600">Product Intelligence</h3>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">{synthesis.product_intelligence}</p>
        </section>
      )}

      {/* ── Evidence Thread ── */}
      {synthesis.evidence_thread.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-3">
            <Shield size={13} className="text-emerald-500" />
            <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-500">Evidence Thread</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {synthesis.evidence_thread.map((e, i) => (
              <EvidenceChip
                key={i}
                claim={e.claim}
                sourceType={e.source_type}
                sourceLabel={e.source_label}
              />
            ))}
          </div>
        </section>
      )}

      {/* ── Risks ── */}
      {synthesis.risks.length > 0 && (
        <section className="rounded-2xl border border-rose-100 bg-rose-50/40 px-6 py-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={13} className="text-rose-400" />
            <h3 className="text-xs font-semibold uppercase tracking-widest text-rose-500">Watch-outs</h3>
          </div>
          <ul className="space-y-1.5">
            {synthesis.risks.map((risk, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-rose-700">
                <span className="mt-1 w-1.5 h-1.5 rounded-full bg-rose-400 flex-shrink-0" />
                {risk}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

// ─── StrategyLens ────────────────────────────────────────────────────────────

export function StrategyLens({
  brief,
  chat,
  recentlyPatched,
  onComposerPrefill,
  onSynthesized,
  onNavigateToAngles,
}: StrategyLensProps) {
  // Default to 'sections' when no synthesis exists yet (initial generation);
  // switch to 'synthesized' once the brief already has a cached overview.
  const [viewMode, setViewMode] = useState<'synthesized' | 'sections'>(
    brief.synthesized_strategy ? 'synthesized' : 'sections',
  );
  const [synthesis, setSynthesis] = useState<SynthesizedStrategy | null>(
    brief.synthesized_strategy ?? null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // True after a background synthesis completes while the user is in Sections view
  const [overviewReadyBanner, setOverviewReadyBanner] = useState(false);
  const fetchedForBriefId = useRef<string | null>(null);
  // Always-current ref so the async synthesis callback can check the live viewMode
  const viewModeRef = useRef(viewMode);
  useEffect(() => { viewModeRef.current = viewMode; }, [viewMode]);

  // Fetch synthesis if not already cached on the brief
  const doSynthesize = useCallback(
    async (force = false) => {
      if (!brief.brief_id) return;
      setLoading(true);
      setError(null);
      try {
        const updated = await generationApi.synthesizeStrategy(brief.brief_id, force);
        if (updated.synthesized_strategy) {
          setSynthesis(updated.synthesized_strategy);
          onSynthesized?.(updated);
          // If the user stayed in Sections view while the overview generated,
          // surface a non-intrusive "ready" banner rather than auto-jumping.
          if (viewModeRef.current === 'sections') {
            setOverviewReadyBanner(true);
          }
        }
      } catch (e) {
        setError((e as Error).message || 'Synthesis failed');
      } finally {
        setLoading(false);
      }
    },
    [brief.brief_id, onSynthesized],
  );

  // Auto-fetch once per brief (unless cached). Runs regardless of viewMode so
  // that synthesis kicks off in the background even when we start on Sections.
  useEffect(() => {
    if (fetchedForBriefId.current === brief.brief_id) return;
    fetchedForBriefId.current = brief.brief_id;

    // Use cached synthesis if inputs_hash still matches — the backend will
    // return the cache instantly without an LLM call.
    const cached = brief.synthesized_strategy;
    if (cached) {
      setSynthesis(cached);
      // Still trigger a background re-validate so icon is generated if missing
      if (!cached.icon_data_url) {
        doSynthesize(false);
      }
    } else {
      doSynthesize(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brief.brief_id]);

  // ── Sections view helpers (preserved from DocumentLens) ─────────────────

  const [feedbackError, setFeedbackError] = useState<string | null>(null);

  const activeDraft: BriefDraftSections | null = useMemo(() => {
    for (let i = chat.messages.length - 1; i >= 0; i--) {
      const msg = chat.messages[i];
      for (const card of msg.cards) {
        if (
          card.kind === 'run' &&
          card.skill === 'brief_draft' &&
          card.status === 'completed'
        ) {
          const payload = card.payload as { sections?: BriefDraftSections };
          if (payload.sections) return payload.sections;
        }
      }
    }
    return null;
  }, [chat.messages]);

  const handleTogglePin = useCallback(
    (section: string) => {
      chat.toggleTarget('section', section);
    },
    [chat],
  );

  const handleDraftSection = useCallback(
    (section: SectionKey) => {
      if (!chat.activeSections.includes(section)) {
        chat.toggleTarget('section', section);
      }
      const sectionLabel =
        SECTION_ORDER.find((s) => s.key === section)?.label ?? section;
      onComposerPrefill(`Draft the ${sectionLabel} section`);
    },
    [chat, onComposerPrefill],
  );

  const handleQuickApply = useCallback(
    async (section: SectionKey, value: string) => {
      if (!chat.token) return;
      const content = mapDraftValueToContent(section, value);
      try {
        const result = await workspaceBriefApi.quickPatch(chat.token, section, content);
        chat.appendSyntheticPatchCard({
          section,
          summary: `Quick-applied draft value for ${section}`,
          before: result.before,
          after: result.after,
        });
      } catch (e) {
        setFeedbackError((e as Error).message || 'Quick-apply failed');
        setTimeout(() => setFeedbackError(null), 4000);
      }
    },
    [chat],
  );

  const briefSnapshot = brief as unknown as BriefSnapshot;

  // ── Toggle button ────────────────────────────────────────────────────────

  const toggleButton = (
    <button
      onClick={() => {
        setViewMode((v) => (v === 'synthesized' ? 'sections' : 'synthesized'));
        // Dismiss the ready banner when the user navigates to the Overview
        if (viewMode === 'sections') setOverviewReadyBanner(false);
      }}
      className={`
        flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all
        ${viewMode === 'sections'
          ? 'bg-white border border-slate-200 text-slate-500 hover:text-slate-800 hover:border-slate-300 shadow-sm'
          : 'bg-slate-900 text-white shadow-sm'
        }
      `}
      title={viewMode === 'synthesized' ? 'Switch to structured sections' : 'Switch to synthesized overview'}
    >
      {viewMode === 'sections' ? (
        loading ? (
          <>
            <Loader2 size={12} className="animate-spin" />
            Overview generating…
          </>
        ) : synthesis ? (
          <>
            <Sparkles size={12} />
            View Overview
          </>
        ) : (
          <>
            <Sparkles size={12} />
            Overview
          </>
        )
      ) : (
        <>
          <Settings2 size={12} />
          Sections
        </>
      )}
    </button>
  );

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="relative flex flex-col min-h-full">
      {/* Floating toggle */}
      <div className="sticky top-0 z-10 flex justify-end px-6 pt-4 pb-0 pointer-events-none">
        <div className="pointer-events-auto">{toggleButton}</div>
      </div>

      {viewMode === 'synthesized' ? (
        <div className="-mt-10">
          {loading && !synthesis && <SynthesisSkeleton />}

          {!loading && error && !synthesis && (
            <div className="max-w-xl mx-auto mt-16 text-center space-y-4">
              <AlertTriangle size={32} className="mx-auto text-rose-400" />
              <p className="text-sm text-slate-600">{error}</p>
              <button
                onClick={() => doSynthesize(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-500 text-white text-sm font-medium hover:bg-teal-600 transition"
              >
                <RefreshCw size={14} />
                Try Again
              </button>
            </div>
          )}

          {synthesis && (
            <SynthesizedView
              synthesis={synthesis}
              onRefresh={() => doSynthesize(true)}
              isRefreshing={loading}
            />
          )}
        </div>
      ) : (
        /* Sections view */
        <div className="flex flex-col gap-0 min-h-full -mt-2">
          {/* ── Generation indicator banner ── */}
          {loading && !synthesis && (
            <div className="mx-6 mt-3 flex items-center gap-3 px-4 py-3 rounded-xl bg-violet-50 border border-violet-200 text-violet-700">
              <Loader2 size={14} className="animate-spin shrink-0 text-violet-500" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold">Generating strategy overview…</p>
                <p className="text-[11px] text-violet-500 mt-0.5">
                  NeuroGraph is synthesizing your brief into a strategic document. Fill in sections below while you wait.
                </p>
              </div>
            </div>
          )}

          {/* ── "Overview ready" banner ── */}
          {overviewReadyBanner && synthesis && !loading && (
            <div className="mx-6 mt-3 flex items-center gap-3 px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800">
              <Sparkles size={14} className="shrink-0 text-emerald-500" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold">Strategy overview ready</p>
                <p className="text-[11px] text-emerald-600 mt-0.5">
                  Your synthesized brief overview is ready to view.
                </p>
              </div>
              <button
                onClick={() => {
                  setOverviewReadyBanner(false);
                  setViewMode('synthesized');
                }}
                className="flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900 transition shrink-0"
              >
                View Overview <ChevronRight size={12} />
              </button>
              <button
                onClick={() => setOverviewReadyBanner(false)}
                className="text-emerald-400 hover:text-emerald-600 transition shrink-0 text-lg leading-none"
                title="Dismiss"
              >
                ×
              </button>
            </div>
          )}

          <div className="flex gap-6 p-6 pt-3">
            <div className="flex-1 min-w-0">
              <BriefCanvas
                headerless
                className="bg-transparent"
                brief={briefSnapshot}
                activeSections={chat.activeSections}
                recentlyPatched={recentlyPatched}
                onTogglePin={handleTogglePin}
                canPin
                activeDraft={activeDraft}
                onDraftSection={handleDraftSection}
                onQuickApply={handleQuickApply}
                canInteract
              />
              {feedbackError && (
                <div className="mt-3 max-w-3xl mx-auto text-[11px] text-rose-600 bg-rose-50 border border-rose-200 rounded px-3 py-2">
                  {feedbackError}
                </div>
              )}
            </div>
            <div className="w-[300px] flex-shrink-0">
              <StrategicGroundingPanel brief={brief} onNavigateToAngles={onNavigateToAngles} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
